import { Link } from '@tanstack/react-router'
import { ColumnDef } from '@tanstack/react-table'
import type {
  LiveEventAdmin,
  LiveEventGameSessionAdmin,
  LiveEventLeaderboardRow,
} from 'api/generated'
import { Badge } from 'components/ui/badge'
import { Button } from 'components/ui/button'
import { Switch } from 'components/ui/switch'
import {
  formatDateTime,
  formatDuration,
  MODE_LABELS,
  STATUS_LABELS,
} from 'helpers/live-events'

export const liveEventsColumns = ({
  onScreenChange,
  onDelete,
}: {
  onScreenChange: (id: number, enabled: boolean) => void
  onDelete: (id: number) => void
}): ColumnDef<LiveEventAdmin>[] => [
  { accessorKey: 'id', header: 'ID' },
  {
    accessorKey: 'name',
    header: 'Evento',
    cell: ({ row }) => (
      <div className='flex flex-col'>
        <Link
          to='/eventi-live/$id'
          params={{ id: row.original.id.toString() }}
          className='font-medium underline-offset-4 hover:underline'
        >
          {row.original.name}
        </Link>
        <span className='text-xs text-muted-foreground'>
          /evento/{row.original.slug}
          {row.original.brand_name ? ` · ${row.original.brand_name}` : ''}
        </span>
      </div>
    ),
  },
  {
    id: 'state',
    header: 'Stato',
    cell: ({ row }) =>
      !row.original.active ? (
        <Badge variant='outline'>Non attivo</Badge>
      ) : row.original.open ? (
        <Badge>Aperto</Badge>
      ) : (
        <Badge variant='secondary'>Fuori orario</Badge>
      ),
  },
  {
    id: 'period',
    header: 'Periodo',
    cell: ({ row }) =>
      `${formatDateTime(row.original.starts_at)} → ${formatDateTime(row.original.ends_at)}`,
  },
  {
    id: 'screen',
    header: 'Maxischermo',
    cell: ({ row }) => (
      <Switch
        checked={row.original.screen_enabled}
        onCheckedChange={(checked) => onScreenChange(row.original.id, checked)}
        aria-label='Maxischermo acceso'
      />
    ),
  },
  {
    id: 'actions',
    header: 'Azioni',
    cell: ({ row }) => (
      <div className='flex items-center gap-2'>
        <Button size='sm' variant='outline' asChild>
          <Link
            to='/eventi-live/$id'
            params={{ id: row.original.id.toString() }}
          >
            Gestisci
          </Link>
        </Button>
        <Button size='sm' variant='outline' asChild>
          <Link
            to='/eventi-live/$id/modifica'
            params={{ id: row.original.id.toString() }}
          >
            Modifica
          </Link>
        </Button>
        <Button
          size='sm'
          variant='destructive'
          onClick={() => {
            if (
              window.confirm(
                `Eliminare l'evento "${row.original.name}" e tutte le sue partite?`
              )
            ) {
              onDelete(row.original.id)
            }
          }}
        >
          Elimina
        </Button>
      </div>
    ),
  },
]

export const liveEventSessionsColumns = ({
  onInvalidate,
  onRestore,
  onDelete,
}: {
  onInvalidate: (session: LiveEventGameSessionAdmin) => void
  onRestore: (session: LiveEventGameSessionAdmin) => void
  onDelete: (session: LiveEventGameSessionAdmin) => void
}): ColumnDef<LiveEventGameSessionAdmin>[] => [
  { accessorKey: 'id', header: 'ID' },
  {
    id: 'user',
    header: 'Utente',
    cell: ({ row }) => (
      <div className='flex flex-col'>
        <span className='font-medium'>{row.original.user.name}</span>
        <span className='text-xs text-muted-foreground'>
          {row.original.user.email} · #{row.original.user.id}
        </span>
      </div>
    ),
  },
  {
    id: 'mode',
    header: 'Modalità',
    cell: ({ row }) => MODE_LABELS[row.original.mode],
  },
  {
    id: 'status',
    header: 'Stato',
    cell: ({ row }) => (
      <div className='flex flex-wrap gap-1'>
        <Badge variant='secondary'>{STATUS_LABELS[row.original.status]}</Badge>
        {row.original.on_screen && <Badge variant='outline'>Schermo</Badge>}
        {row.original.stats?.suspicious === true && (
          <Badge variant='destructive'>Sospetta</Badge>
        )}
        {row.original.invalidated && (
          <Badge variant='destructive'>Annullata</Badge>
        )}
        {row.original.deleted && <Badge variant='outline'>Eliminata</Badge>}
      </div>
    ),
  },
  {
    id: 'score',
    header: 'Punti',
    cell: ({ row }) => (
      <div className='flex flex-col tabular-nums'>
        <span className='font-semibold'>{row.original.score ?? '—'}</span>
        <span className='text-xs text-muted-foreground'>
          {row.original.hits ?? 0} colpi · {row.original.bonus_hits ?? 0}{' '}
          bersagli · liv. {row.original.level ?? '—'} ·{' '}
          {formatDuration(row.original.duration_ms)}
        </span>
      </div>
    ),
  },
  {
    id: 'dates',
    header: 'Date',
    cell: ({ row }) => (
      <div className='flex flex-col text-xs text-muted-foreground'>
        <span>In coda: {formatDateTime(row.original.queued_at)}</span>
        <span>Fine: {formatDateTime(row.original.finished_at)}</span>
        {row.original.invalidated_reason && (
          <span>Motivo: {row.original.invalidated_reason}</span>
        )}
      </div>
    ),
  },
  {
    id: 'actions',
    header: 'Azioni',
    cell: ({ row }) => {
      const session = row.original
      const removable = !session.invalidated && !session.deleted
      return (
        <div className='flex items-center gap-2'>
          {removable ? (
            <>
              <Button
                size='sm'
                variant='outline'
                onClick={() => onInvalidate(session)}
              >
                Annulla
              </Button>
              <Button
                size='sm'
                variant='destructive'
                onClick={() => {
                  if (window.confirm('Eliminare questa partita?')) {
                    onDelete(session)
                  }
                }}
              >
                Elimina
              </Button>
            </>
          ) : (
            <Button
              size='sm'
              variant='outline'
              onClick={() => onRestore(session)}
            >
              Ripristina
            </Button>
          )}
        </div>
      )
    },
  },
]

export const liveEventLeaderboardColumns: ColumnDef<LiveEventLeaderboardRow>[] =
  [
    { accessorKey: 'position', header: '#' },
    {
      id: 'user',
      header: 'Giocatore',
      cell: ({ row }) => `${row.original.user.name} (#${row.original.user.id})`,
    },
    { accessorKey: 'score', header: 'Punti' },
    {
      id: 'level',
      header: 'Livello',
      cell: ({ row }) => row.original.level ?? '—',
    },
    {
      id: 'finished_at',
      header: 'Data',
      cell: ({ row }) => formatDateTime(row.original.finished_at),
    },
    { accessorKey: 'session_id', header: 'Partita' },
  ]
